<div class="marketing-leads-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Marketing Leads</h1>
        <p>Manage and view all marketing leads</p>
      </div>
    </div>
  </div>

  <!-- Filter Section -->
  <div class="filter-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Leads</h3>
      <button class="clear-filters-btn" (click)="clearFilters()" [disabled]="!hasActiveFilters()">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>

    <div class="filter-form">
      <div class="filter-row">
        <div class="filter-group">
          <label for="geoZoneFilter">Geo Zone <span class="required">*</span></label>
          <select id="geoZoneFilter" [(ngModel)]="filters.geoZoneId" class="filter-select" required>
            <option value="">Select Geo Zone</option>
            <option *ngFor="let zone of zones" [value]="zone.id">
              {{ zone.name }}
            </option>
          </select>
          <div *ngIf="loadingZones" class="loading-place-types">
            <i class="fas fa-spinner fa-spin"></i> Loading zones...
          </div>
        </div>

        <div class="filter-group">
          <label for="placeTypeFilter">Place Type</label>
          <select id="placeTypeFilter" [(ngModel)]="filters.placeType" class="filter-select">
            <option value="">Select Place Type</option>
            <optgroup *ngFor="let group of groupedPlaceTypes" [label]="group.category">
              <option *ngFor="let placeType of group.types" [value]="placeType.name">
                {{ placeType.displayName || placeType.name }}
              </option>
            </optgroup>
          </select>
          <div *ngIf="loadingPlaceTypes" class="loading-place-types">
            <i class="fas fa-spinner fa-spin"></i> Loading place types...
          </div>
        </div>

        <div class="filter-group">
          <label for="statusFilter">Status</label>
          <select id="statusFilter" [(ngModel)]="filters.status" class="filter-select">
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Converted">Converted</option>
            <option value="Rejected">Rejected</option>
            <option value="Invalid">Invalid</option>
            <option value="FollowUp">FollowUp</option>
          </select>
        </div>
      </div>

      <div class="filter-row">
        <div class="filter-actions">
          <button class="apply-filters-btn" (click)="applyFilters()" [disabled]="!canLoadLeads()">
            <i class="fas fa-search"></i> Load Leads
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div class="error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>

  <!-- Success Message -->
  <div class="success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading" *ngIf="loading && !leadsResponse">
    <p><i class="fas fa-spinner fa-spin"></i> Loading leads...</p>
  </div>

  <!-- Leads Table -->
  <div class="table-container" *ngIf="leadsResponse">
    <!-- Pagination Summary -->
    <div class="pagination-summary">
      <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount || 0) }} of {{
        totalCount || 0 }} leads</p>
    </div>

    <table class="leads-table">
      <thead>
        <tr>
          <th class="col-business">Business Name</th>
          <th class="col-phone">Phone Number</th>
          <th class="col-status">Status</th>
          <th class="col-updated">Last Updated</th>
          <th class="col-actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let lead of leadsResponse.items || []" class="lead-row">
          <td class="col-business">
            <strong>{{ lead.name || 'N/A' }}</strong>
            <div class="place-types" *ngIf="lead.types && lead.types.length > 0">
              <span class="place-type-badge" *ngFor="let type of lead.types">{{ type }}</span>
            </div>
            <div class="address-preview" *ngIf="lead.address">{{ lead.address }}</div>
          </td>
          <td class="col-phone">
            <div class="phone-number" *ngIf="lead.phoneNumber; else noPhone">
              {{ lead.phoneNumber }}
            </div>
            <ng-template #noPhone>
              <span class="no-data">N/A</span>
            </ng-template>
          </td>
          <td class="col-status">
            <span [class]="getStatusBadgeClass(lead.status)">{{ lead.status || 'N/A' }}</span>
          </td>
          <td class="col-updated">
            <div class="last-updated" *ngIf="lead.lastSyncedAt">
              {{ lead.lastSyncedAt | date:'short' }}
            </div>
            <span *ngIf="!lead.lastSyncedAt" class="no-data">N/A</span>
          </td>
          <td class="col-actions">
            <div class="action-buttons">
              <button class="view-btn" (click)="viewLeadDetails(lead)" title="View Details">
                <i class="fas fa-eye"></i> View
              </button>
              <!-- Update Status only available to users with Marketing_Manage (Telemarketer / CCAdmin) -->
              <button class="update-status-btn" (click)="openUpdateStatusModal(lead)" title="Update Status"
                *appHasPermission="['Marketing_Manage', 'Telemarketer_Permissions']">
                <i class="fas fa-edit"></i> Update Status
              </button>
            </div>
          </td>
        </tr>
        <tr *ngIf="!leadsResponse.items || leadsResponse.items.length === 0">
          <td colspan="5" class="no-leads">
            <p>No leads found.</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination Component -->
  <app-pagination *ngIf="leadsResponse" [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
    (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
  </app-pagination>

  <!-- Lead Details Panel -->
  <div class="details-panel-overlay" *ngIf="showDetailsPanel" (click)="closeDetailsPanel()">
    <div class="details-panel" (click)="$event.stopPropagation()">
      <div class="details-header">
        <h2>Lead Details</h2>
        <button class="close-btn" (click)="closeDetailsPanel()">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="details-body" *ngIf="selectedLead">
        <div class="detail-section">
          <h3>Business Information</h3>
          <div class="detail-item">
            <label>Business Name:</label>
            <span>{{ selectedLead.name || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <label>Address:</label>
            <span>{{ selectedLead.address || 'N/A' }}</span>
          </div>
          <div class="detail-item" *ngIf="selectedLead.phoneNumber">
            <label>Phone Number:</label>
            <span>{{ selectedLead.phoneNumber }}</span>
          </div>
          <div class="detail-item" *ngIf="selectedLead.website">
            <label>Website:</label>
            <span><a [href]="selectedLead.website" target="_blank" rel="noopener noreferrer">{{ selectedLead.website
                }}</a></span>
          </div>
          <div class="detail-item">
            <label>Place Types:</label>
            <div class="place-types">
              <span class="place-type-badge" *ngFor="let type of selectedLead.types || []">{{ type }}</span>
              <span *ngIf="!selectedLead.types || selectedLead.types.length === 0" class="no-data">N/A</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3>Location</h3>
          <div class="detail-item" *ngIf="selectedLead.latitude !== undefined && selectedLead.longitude !== undefined">
            <label>Coordinates:</label>
            <span>{{ selectedLead.latitude | number:'1.6-6' }}, {{ selectedLead.longitude | number:'1.6-6' }}</span>
          </div>
        </div>

        <div class="detail-section">
          <h3>Status Information</h3>
          <div class="detail-item">
            <label>Current Status:</label>
            <span [class]="getStatusBadgeClass(selectedLead.status)">{{ selectedLead.status || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <label>Last Synced:</label>
            <span *ngIf="selectedLead.lastSyncedAt">{{ selectedLead.lastSyncedAt | date:'short' }}</span>
            <span *ngIf="!selectedLead.lastSyncedAt" class="no-data">N/A</span>
          </div>
        </div>

        <div class="detail-section" *ngIf="selectedLead.remarks && selectedLead.remarks.length > 0">
          <h3>Remarks</h3>
          <div class="remarks-history">
            <div class="remark-item" *ngFor="let remark of selectedLead.remarks">
              <div class="remark-header">
                <span [class]="getStatusBadgeClass(remark.status)">{{ remark.status }}</span>
                <span class="remark-date">{{ remark.createdAt | date:'short' }}</span>
              </div>
              <div class="remark-text">{{ remark.comment }}</div>
              <div class="remark-meta">
                <span>by {{ remark.createdBy }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Update Status Modal -->
  <div class="modal-overlay" *ngIf="showUpdateStatusModal" (click)="closeUpdateStatusModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2>Update Lead Status</h2>
        <button class="close-btn" (click)="closeUpdateStatusModal()">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body" *ngIf="leadForStatusUpdate">
        <div class="lead-info">
          <p><strong>Business:</strong> {{ leadForStatusUpdate.name }}</p>
          <p><strong>Place ID:</strong> {{ leadForStatusUpdate.placeId }}</p>
        </div>

        <form (ngSubmit)="updateLeadStatus()">
          <div class="form-group">
            <label for="status">Status *</label>
            <select id="status" [(ngModel)]="updateStatusForm.status" name="status" class="form-control" required>
              <option value="">Select Status</option>
              <option value="Pending">Pending</option>
              <option value="Converted">Converted</option>
              <option value="Rejected">Rejected</option>
              <option value="Invalid">Invalid</option>
              <option value="FollowUp">FollowUp</option>
            </select>
          </div>

          <div class="form-group">
            <label for="remark">Remark</label>
            <textarea id="remark" [(ngModel)]="updateStatusForm.remark" name="remark"
              placeholder="Enter remark (optional)..." rows="4" class="form-control">
            </textarea>
          </div>

          <div class="error-message" *ngIf="errorMessage">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
          </div>

          <div class="success-message" *ngIf="successMessage">
            <i class="fas fa-check-circle"></i> {{ successMessage }}
          </div>

          <div class="modal-actions">
            <button type="button" class="cancel-btn" (click)="closeUpdateStatusModal()">Cancel</button>
            <button type="submit" class="save-btn" [disabled]="loading || !updateStatusForm.status.trim()">
              <i class="fas fa-spinner fa-spin" *ngIf="loading"></i>
              <span *ngIf="!loading">Save</span>
              <span *ngIf="loading">Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>